import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, FolderOpen } from 'lucide-react';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    domains: '',
    synonyms: '',
    status: 'active'
  });

  const statusOptions = [
    { value: 'active', label: 'Активный', color: '#10b981' },
    { value: 'completed', label: 'Завершён', color: '#3b82f6' },
    { value: 'paused', label: 'Приостановлен', color: '#f59e0b' }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/projects');
      setProjects(response.data);
      setError('');
    } catch (error) {
      setError('Ошибка при загрузке проектов');
      console.error('Fetch projects error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openModal = (project = null) => {
    setEditingProject(project);
    setFormData(project ? {
      title: project.title,
      description: project.description || '',
      domains: project.domains || '',
      synonyms: project.synonyms || '',
      status: project.status
    } : {
      title: '',
      description: '',
      domains: '',
      synonyms: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({ title: '', description: '', domains: '', synonyms: '', status: 'active' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Название проекта обязательно');
      return;
    }

    try {
      if (editingProject) {
        // Update project
        const response = await axios.put(`/api/projects/${editingProject.id}`, formData);
        setProjects(projects.map(p =>
          p.id === editingProject.id ? response.data : p
        ));
      } else {
        // Create project
        const response = await axios.post('/api/projects', formData);
        setProjects([response.data, ...projects]);
        // Automatically select the new project
        localStorage.setItem('selectedProject', response.data.id);
      }

      closeModal();
      setError('');
    } catch (error) {
      setError(error.response?.data?.message || 'Ошибка при сохранении проекта');
    }
  };

  const handleDelete = async (projectId, e) => {
    e.stopPropagation(); // Prevent card click
    if (!window.confirm('Вы уверены, что хотите удалить этот проект?')) {
      return;
    }

    try {
      await axios.delete(`/api/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      setError('');
    } catch (error) {
      setError('Ошибка при удалении проекта');
    }
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(option => option.value === status) || statusOptions[0];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="projects-container">
        <div className="loading">Загрузка проектов...</div>
      </div>
    );
  }

  return (
    <div className="projects-container">
      <div className="projects-header">
        <div className="header-content">
          <h1>Проекты</h1>
          <p>Управление вашими проектами</p>
        </div>
        <button className="btn-primary" onClick={() => openModal()}>
          <Plus size={20} />
          Создать проект
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {projects.length === 0 ? (
        <div className="empty-state">
          <FolderOpen size={48} />
          <h3>Пока нет проектов</h3>
          <p>Создайте свой первый проект, чтобы начать работу</p>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => {
            const statusInfo = getStatusInfo(project.status);
            return (
              <div key={project.id} className="project-card" onClick={() => openModal(project)} style={{ cursor: 'pointer' }}>
                <div className="project-header">
                  <h3 className="project-title">{project.title}</h3>
                  <div className="project-actions">
                    <button
                      className="btn-icon"
                      onClick={(e) => { e.stopPropagation(); openModal(project); }}
                      title="Редактировать"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={(e) => handleDelete(project.id, e)}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {project.description && (
                  <p className="project-description">{project.description}</p>
                )}

                <div className="project-footer">
                  <div
                    className="project-status"
                    style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}
                  >
                    {statusInfo.label}
                  </div>
                  <div className="project-date">
                    {formatDate(project.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProject ? 'Редактировать проект' : 'Создать проект'}</h2>
              <button className="btn-icon" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="title">Название проекта *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Введите название проекта"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Описание</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Введите описание проекта"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="domains">Домены (через запятую)</label>
                  <textarea
                    id="domains"
                    name="domains"
                    value={formData.domains}
                    onChange={handleInputChange}
                    placeholder="example.com, site.ru"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="synonyms">Синонимы бренда (через запятую)</label>
                  <textarea
                    id="synonyms"
                    name="synonyms"
                    value={formData.synonyms}
                    onChange={handleInputChange}
                    placeholder="Бренд, Компания, BrandName"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Статус</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={20} />
                  {editingProject ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
